/**
 * Mission Detail Page
 * Display comprehensive mission information
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { MissionService } from '../../../services/mission/MissionService';
import type { MissionDetail } from '../../../types/mission';
import { RefundDecision, MissionStatus } from '../../../types/mission';
import {
  Card,
  CardContent,
  TextField,
  PrimaryButton,
} from '../../../components/ui';
import { OverviewTab } from './components/tabs/OverviewTab';
import { TimelineTab } from './components/tabs/TimelineTab';
import { IntakeTab } from './components/tabs/IntakeTab';
import { MessageTab } from './components/tabs/MessageTab';
import { DebriefTab } from './components/tabs/DebriefTab';

type TabValue = 'overview' | 'timeline' | 'intake' | 'message' | 'debrief';

const isRefundDecisionSelectable = (paymentStatus?: string | null): boolean =>
  paymentStatus === 'SettlementSubmitted' || paymentStatus === 'Settled';

const isVoidOnlyPaymentStatus = (paymentStatus?: string | null): boolean =>
  paymentStatus === 'Authorized';

const getDefaultRefundDecision = (paymentStatus?: string | null): RefundDecision =>
  isRefundDecisionSelectable(paymentStatus) ? RefundDecision.Denied : RefundDecision.None;

const getCancelDialogDescription = (paymentStatus?: string | null): string => {
  if (paymentStatus === 'Authorized') {
    return 'Are you sure you want to cancel this mission? The current payment authorization will be voided automatically.';
  }

  if (paymentStatus === 'SettlementSubmitted') {
    return 'Are you sure you want to cancel this mission? The system will check the latest provider status and either void the authorization or apply the selected refund decision.';
  }

  if (paymentStatus === 'Settled') {
    return 'Are you sure you want to cancel this mission? The selected refund decision will be applied to the settled payment.';
  }

  return 'Are you sure you want to cancel this mission? This action cannot be undone.';
};

const MissionDetailPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [mission, setMission] = useState<MissionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [refundDecision, setRefundDecision] = useState<RefundDecision>(RefundDecision.None);
  const [refundAmount, setRefundAmount] = useState(0);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const refreshMissionDetail = useCallback(async (missionId: string) => {
    try {
      setIsLoading(true);
      const detail = await MissionService.getMissionDetail(missionId);
      setMission(detail);
      setReviewError(null);
    } catch (error) {
      console.error('Failed to fetch mission detail:', error);
      setMission(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    refreshMissionDetail(id);
  }, [id, refreshMissionDetail]);

  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
    setCancelReason('');
    setRefundDecision(getDefaultRefundDecision(mission?.latestPaymentStatus));
    setRefundAmount(0);
    setCancelError(null);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason('');
    setRefundDecision(getDefaultRefundDecision(mission?.latestPaymentStatus));
    setRefundAmount(0);
    setCancelError(null);
  };

  const handleOpenRejectDialog = () => {
    setRejectDialogOpen(true);
    setRejectReason('');
    setReviewError(null);
  };

  const handleOpenApproveDialog = () => {
    setApproveDialogOpen(true);
    setReviewError(null);
  };

  const handleCloseApproveDialog = () => {
    setApproveDialogOpen(false);
    setReviewError(null);
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectReason('');
    setReviewError(null);
  };

  const handleCancelMission = async () => {
    if (!mission || !cancelReason.trim()) return;

    try {
      setIsCancelling(true);
      setCancelError(null);
      const shouldSelectRefundDecision = isRefundDecisionSelectable(mission.latestPaymentStatus);
      await MissionService.cancelMission(mission.id, {
        reason: cancelReason.trim(),
        decision: shouldSelectRefundDecision ? refundDecision : RefundDecision.None,
        refundAmount: shouldSelectRefundDecision ? refundAmount : 0,
      });
      await refreshMissionDetail(mission.id);
      handleCloseCancelDialog();
    } catch (error) {
      console.error('Failed to cancel mission:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel mission. Please try again.';
      setCancelError(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleApproveReview = async () => {
    if (!mission) return;

    try {
      setIsApproving(true);
      setReviewError(null);
      await MissionService.approveMissionReview(mission.id);
      await refreshMissionDetail(mission.id);
      handleCloseApproveDialog();
    } catch (error) {
      console.error('Failed to approve mission review:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve mission review. Please try again.';
      setReviewError(errorMessage);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejectReview = async () => {
    if (!mission || !rejectReason.trim()) return;

    try {
      setIsRejecting(true);
      setReviewError(null);
      await MissionService.rejectMissionReview(mission.id, rejectReason.trim());
      await refreshMissionDetail(mission.id);
      handleCloseRejectDialog();
    } catch (error) {
      console.error('Failed to reject mission review:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject mission review. Please try again.';
      setReviewError(errorMessage);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!mission) {
    return (
      <MainLayout>
        <EmptyState
          title="Mission Not Found"
          message="The mission you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  const canShowCancel = ![
    MissionStatus.PENDING_ADMIN_REVIEW,
    MissionStatus.COMPLETED,
    MissionStatus.UNDER_REVIEW,
    MissionStatus.CANCELLED,
    MissionStatus.EXPIRED,
    MissionStatus.CLOSED,
  ].includes(mission.status);
  const canSelectRefundDecision = isRefundDecisionSelectable(mission.latestPaymentStatus);
  const isVoidOnlyCancel = isVoidOnlyPaymentStatus(mission.latestPaymentStatus);
  const requiresRefundAmount = canSelectRefundDecision && refundDecision === RefundDecision.Partial;
  const hasInvalidRefundAmount = requiresRefundAmount
    && (refundAmount <= 0 || refundAmount > (mission.totalPrice || 0));

  const dialogActionButtonSx = {
    borderRadius: theme.shapes.borderRadius.md,
    padding: theme.spacing(1, 3),
    minWidth: 140,
  };

  const approveActionButtonSx = {
    ...dialogActionButtonSx,
    backgroundColor: theme.palette.mode === 'dark' ? '#2F7F66' : '#2E8B68',
    color: '#F5FFFB',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#3B9477' : '#277A5B',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
      color: theme.palette.action.disabled,
    },
  };

  const rejectActionButtonSx = {
    ...dialogActionButtonSx,
    backgroundColor: theme.palette.mode === 'dark' ? '#A14F63' : '#B85A70',
    color: '#FFF8FA',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#B35E74' : '#A44D62',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
      color: theme.palette.action.disabled,
    },
  };

  const dialogCancelButtonSx = {
    ...dialogActionButtonSx,
    backgroundColor: theme.palette.mode === 'dark' ? '#2D3138' : '#EEF2F6',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#363B43' : '#E4E9EF',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
      color: theme.palette.action.disabled,
    },
  };

  const headerCancelButtonSx = {
    minWidth: 132,
    height: 40,
    padding: theme.spacing(1, 2.5),
    borderRadius: '999px',
    background: 'linear-gradient(180deg, #FF694C 0%, #FF5A3D 56%, #F14A32 100%)',
    color: '#FFF7F3',
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 700,
    letterSpacing: '0.01em',
    boxShadow: '0 10px 22px rgba(241, 74, 50, 0.24), inset 0 1px 0 rgba(255,255,255,0.18)',
    border: '1px solid rgba(255,255,255,0.08)',
    textTransform: 'none' as const,
    '&:hover': {
      background: 'linear-gradient(180deg, #FF7558 0%, #FF6244 56%, #F4543A 100%)',
      boxShadow: '0 12px 26px rgba(241, 74, 50, 0.3), inset 0 1px 0 rgba(255,255,255,0.18)',
    },
    '&.Mui-disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      boxShadow: 'none',
      border: 'none',
    },
  };

  return (
    <MainLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Mission Title: {mission.title || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
          {mission.canApproveReview && (
            <PrimaryButton
              startIcon={<ApproveIcon />}
              onClick={handleOpenApproveDialog}
              disabled={isApproving || isRejecting}
              sx={approveActionButtonSx}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </PrimaryButton>
          )}
          {mission.canRejectReview && (
            <PrimaryButton
              startIcon={<RejectIcon />}
              onClick={handleOpenRejectDialog}
              disabled={isApproving || isRejecting}
              sx={rejectActionButtonSx}
            >
              Reject
            </PrimaryButton>
          )}
          {canShowCancel && (
            <PrimaryButton
              onClick={handleOpenCancelDialog}
              sx={headerCancelButtonSx}
            >
              Cancel Mission
            </PrimaryButton>
          )}
        </Box>
      </Box>

      {reviewError && (
        <Box
          sx={{
            padding: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            borderRadius: theme.shape.borderRadius,
            fontSize: '0.875rem',
          }}
        >
          {reviewError}
        </Box>
      )}

      <Card>
        <CardContent noPadding>
          <Box sx={{ borderBottom: 1, borderColor: theme.customColors.tableBorder }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Tab label="Overview" value="overview" />
              <Tab label="Timeline" value="timeline" />
              <Tab label="Intake" value="intake" />
              <Tab label="Message" value="message" />
              <Tab label="Debrief" value="debrief" />
            </Tabs>
          </Box>

          <Box sx={{ padding: theme.spacing(3) }}>
            {activeTab === 'overview' && <OverviewTab mission={mission} />}
            {activeTab === 'timeline' && <TimelineTab mission={mission} />}
            {activeTab === 'intake' && <IntakeTab mission={mission} />}
            {activeTab === 'message' && <MessageTab mission={mission} />}
            {activeTab === 'debrief' && <DebriefTab mission={mission} />}
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={approveDialogOpen}
        onClose={isApproving ? undefined : handleCloseApproveDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Approve Mission
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            Are you sure you want to approve this mission? It will move into the submitted state and start matching immediately.
          </Typography>
          {reviewError && (
            <Box
              sx={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
              }}
            >
              {reviewError}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
          <PrimaryButton
            onClick={handleCloseApproveDialog}
            disabled={isApproving}
            sx={{
              ...dialogCancelButtonSx,
              color: theme.palette.text.primary,
            }}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={handleApproveReview}
            disabled={isApproving}
            sx={approveActionButtonSx}
          >
            {isApproving ? 'Approving...' : 'Approve'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialogOpen}
        onClose={isRejecting ? undefined : handleCloseRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Reject Mission
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            Are you sure you want to reject this mission? This will close it and void or refund the payment based on its current payment state.
          </Typography>
          {reviewError && (
            <Box
              sx={{
                padding: theme.spacing(1.5),
                marginBottom: theme.spacing(2),
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
              }}
            >
              {reviewError}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason"
            placeholder="Please provide a reason for rejecting this mission..."
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              setReviewError(null);
            }}
            required
            error={rejectDialogOpen && !rejectReason.trim()}
            helperText={rejectDialogOpen && !rejectReason.trim() ? 'Reject reason is required' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
          <PrimaryButton
            onClick={handleCloseRejectDialog}
            sx={{
              ...dialogCancelButtonSx,
              color: theme.palette.text.primary,
            }}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={handleRejectReview}
            disabled={!rejectReason.trim() || isRejecting}
            sx={rejectActionButtonSx}
          >
            {isRejecting ? 'Rejecting...' : 'Reject'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onClose={isCancelling ? undefined : handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Cancel Mission
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            {getCancelDialogDescription(mission.latestPaymentStatus)}
          </Typography>
          {cancelError && (
            <Box
              sx={{
                padding: theme.spacing(1.5),
                marginBottom: theme.spacing(2),
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
              }}
            >
              {cancelError}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Cancellation Reason"
            placeholder="Please provide a reason for cancelling this mission..."
            value={cancelReason}
            disabled={isCancelling}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setCancelError(null);
            }}
          />

          {isVoidOnlyCancel && (
            <Box
              sx={{
                marginTop: theme.spacing(2),
                padding: theme.spacing(1.5),
                backgroundColor: theme.customColors.searchBackground,
                borderRadius: 1,
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                Latest payment status is <strong>Authorized</strong>. The system will void the authorization automatically if the cancellation succeeds.
              </Typography>
            </Box>
          )}

          {canSelectRefundDecision && (
            <Box sx={{ display: 'flex', gap: theme.spacing(2), marginTop: theme.spacing(2) }}>
              <FormControl fullWidth>
                <InputLabel>Refund Decision</InputLabel>
                <Select
                  value={refundDecision}
                  label="Refund Decision"
                  disabled={isCancelling}
                  onChange={(e) => {
                    const decision = e.target.value as RefundDecision;
                    setRefundDecision(decision);
                    if (decision === RefundDecision.Denied) {
                      setRefundAmount(0);
                    } else if (mission.totalPrice) {
                      setRefundAmount(mission.totalPrice);
                    }
                  }}
                >
                  <MenuItem value={RefundDecision.Full}>Full Refund</MenuItem>
                  <MenuItem value={RefundDecision.Partial}>Partial Refund</MenuItem>
                  <MenuItem value={RefundDecision.Denied}>No Refund</MenuItem>
                </Select>
              </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label="Refund Amount"
                  value={refundAmount}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    const maxAmount = mission.totalPrice || 0;
                    if (value >= 0 && value <= maxAmount) {
                      setRefundAmount(value);
                    }
                  }}
                  disabled={
                    isCancelling ||
                    refundDecision === RefundDecision.Denied || refundDecision === RefundDecision.Full
                  }
                  error={hasInvalidRefundAmount}
                  inputProps={{
                    min: 0,
                    max: mission.totalPrice || 0,
                  }}
                  helperText={
                    refundDecision === RefundDecision.Denied
                      ? 'Amount set to 0 for denied refunds'
                      : refundDecision === RefundDecision.Full
                        ? 'Amount automatically set to total price for full refunds'
                        : refundAmount <= 0
                          ? 'Amount must be greater than 0'
                          : refundAmount > (mission.totalPrice || 0)
                            ? 'Amount cannot exceed total price'
                            : `Max: $${mission.totalPrice || 0}`
                  }
                />
              </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
          <PrimaryButton
            onClick={handleCloseCancelDialog}
            disabled={isCancelling}
            sx={{
              ...dialogCancelButtonSx,
              color: theme.palette.text.primary,
            }}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={handleCancelMission}
            disabled={
              !cancelReason.trim() ||
              hasInvalidRefundAmount ||
              isCancelling
            }
            sx={rejectActionButtonSx}
          >
            {isCancelling ? 'Cancelling...' : 'Confirm Cancel'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default MissionDetailPage;
